import {AxiosResponse} from 'axios'

import i18n from '@/i18n'
import {DealerUser, Lead, LeadRequest, LeadStatus, LeadSummary, LogItem} from '@/types'
import {SalesAppointmentResponse} from '@/types/sales-appointments'
import {processOptionsLeadCommentLog, processOptionsLeadSummaryCommentLog} from '@/utils/utils'

import ApiGatewayClient from './aws/aws-gateway-client'

export class RidoApi {
  private readonly client: ApiGatewayClient

  constructor() {
    this.client = new ApiGatewayClient()
  }

  async getLeads(): Promise<AxiosResponse<{data: Array<LeadSummary>}>> {
    const response = await this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'GET',
      path: `/v1/iam/leads`,
    })

    if (response.data?.data) {
      const currentLocale = i18n.locale as 'es' | 'en' | 'pt'
      response.data.data = response.data.data.map((lead: LeadSummary) =>
        processOptionsLeadSummaryCommentLog(lead, currentLocale),
      )
    }

    return response
  }

  async getLead(id: string): Promise<AxiosResponse<Lead>> {
    const response = await this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'GET',
      path: `/v1/iam/leads/${id}`,
    })

    if (response.data) {
      const currentLocale = i18n.locale as 'es' | 'en' | 'pt'
      response.data = processOptionsLeadCommentLog(response.data, currentLocale)
    }

    return response
  }

  addOffer(id: string, offerId: string): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'PATCH',
      path: `/v1/iam/leads/${id}`,
      data: {offerId},
    })
  }

  markAsRead(id: string, read: boolean): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'PATCH',
      path: `/v1/iam/leads/${id}`,
      data: {read},
    })
  }

  updateLeadStatus(id: string, status: LeadStatus): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'PATCH',
      path: `/v1/iam/leads/${id}`,
      data: {status},
    })
  }

  updateComments(id: string, commentLog: Array<LogItem>): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'PATCH',
      path: `/v1/iam/leads/${id}`,
      data: {commentLog},
    })
  }

  updateState(id: string, status: LeadStatus): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'PATCH',
      path: `/v1/iam/leads/${id}`,
      data: {status},
    })
  }

  assignLead(id: string, assigneeEmail: string): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'PATCH',
      path: `/v1/iam/leads/${id}/assignment`,
      data: {assigneeEmail},
    })
  }

  createLead(leadRequest: LeadRequest): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'POST',
      path: `/v1/iam/leads`,
      data: leadRequest,
    })
  }

  getLeadSalesAppointments(leadId: string): Promise<AxiosResponse<SalesAppointmentResponse[]>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'GET',
      path: `/v1/iam/leads/${leadId}/sales-appointments`,
    })
  }

  getLeadsByDealerContact(dealerContactId: string): Promise<AxiosResponse<any>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'GET',
      path: `/v1/iam/leads/dealer-contact/${dealerContactId}`,
    })
  }

  listDealerUsers(dealerId: string): Promise<AxiosResponse<{data: Array<DealerUser>}>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'GET',
      path: `/v1/iam/dealers/${dealerId}/users`,
    })
  }

  createSalesAppointment(payload: {
    leadId: string
    appointmentType: string
    startDate: string
    endDate: string
    comment?: string
    product?: {
      model: string
    }
  }): Promise<AxiosResponse<{id: string}>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'POST',
      path: `/v1/iam/sales-appointments`,
      data: payload,
    })
  }

  createSalesAppointmentAndLead(payload: {
    contact: {
      name: string
      phone: string
      email?: string
    }
    appointmentType: string
    startDate: string
    endDate: string
    comment?: string
    product?: {
      model: string
    }
  }): Promise<AxiosResponse<{id: string}>> {
    return this.client.sendRequest({
      endpoint: process.env.VUE_APP_MAPIT_RIDO_API,
      method: 'POST',
      path: `/v1/iam/sales-appointments-and-lead`,
      data: payload,
    })
  }
}

export default new RidoApi()
